import { UserMessageAttachments } from "@/components/assistant-ui/attachment";
import { MarkdownText } from "@/components/assistant-ui/markdown-text";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  HIGHLIGHT_CONFIG,
  HighlightToolResult,
} from "@/src/pages/study/components/layout/highlights/highlights";
import {
  TOOL_PREFIX,
  TRIAL_GATE_TOOL,
} from "@/src/pages/study/hooks/useChatRuntime";
import {
  ActionBarPrimitive,
  AssistantIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePartState,
  MessagePrimitive,
  ThreadPrimitive,
  useAssistantApi,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  PencilIcon,
  RefreshCwIcon,
  Sparkles,
  SquareIcon,
  RectangleHorizontal,
  SquareCheckBig,
  XIcon,
  SettingsIcon,
  FileText,
} from "lucide-react";
import type { FC } from "react";
import { useState, useRef, useEffect } from "react";
import { IntentAwareText } from "./intent-aware-text";
import { HighlightButton } from "@/src/pages/study/components/layout/highlights/highlight-button";
import { useAuth } from "@/lib/auth";
import createdGuyUrl from "../../images/study/created.png";
import creatingGuyUrl from "../../images/study/creating.png";
import thinkingGuyUrl from "../../images/study/thinking.png";
import thoughtGuyUrl from "../../images/study/thought.png";
import { useNotebookContext } from "@/src/pages/study/hooks/useNotebook";
import { GenerationConfigControls } from "./generation-controls";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { SelectedSourcesPill } from "./selected-sources";
import { Dialog, DialogTrigger } from "../ui/dialog";
import AINutritionDialogContent from "@/src/pages/study/components/layout/ai-nutrition";
import AIThinkingAnimation from "./thinking-animation";
import { TrialGateToolResult } from "@/src/pages/study/components/layout/highlights/trial-gate-tool";

const createdGuyImgSrc = new URL(createdGuyUrl, import.meta.url).href;
const creatingGuyImgSrc = new URL(creatingGuyUrl, import.meta.url).href;
const thinkingGuyImgSrc = new URL(thinkingGuyUrl, import.meta.url).href;
const thoughtGuyImgSrc = new URL(thoughtGuyUrl, import.meta.url).href;

export const Thread: FC = () => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root @container flex h-full flex-col bg-theme-0"
      style={{
        ["--thread-max-width" as string]: "44rem",
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth px-4 pt-4 text-sm sm:text-base"
      >
        <AssistantIf condition={({ thread }) => thread.isEmpty}>
          <ThreadWelcome />
        </AssistantIf>

        <ThreadPrimitive.Messages
          components={{
            UserMessage,
            EditComposer,
            AssistantMessage,
          }}
        />

        <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-(--thread-max-width) flex-col overflow-visible rounded-t-3xl pt-10">
          <ThreadScrollToBottom />
          <AssistantIf condition={({ thread }) => !thread.isEmpty}>
            <ThreadSuggestions />
          </AssistantIf>
          <div className="bg-theme-0 pb-4 md:pb-6">
            <Composer />
            <p className="mt-2 text-xs text-gray-500 text-center">
              <Dialog>
                <DialogTrigger className="underline">
                  AI can make mistakes
                </DialogTrigger>
                <AINutritionDialogContent />
              </Dialog>
              {". "}
              Always verify important information.
            </p>
          </div>
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom -top-12 absolute z-10 self-center rounded-full p-4 disabled:invisible bg-white dark:bg-theme-0 dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  const { authUser } = useAuth();
  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="aui-thread-welcome-center flex w-full grow flex-col items-center justify-center">
        <div className="aui-thread-welcome-message flex size-full flex-col justify-center px-4">
          <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in font-semibold text-2xl duration-200 text-theme-0">
            Hello {(authUser.user_metadata.name || "there").split(" ")[0]}!
          </h1>
          <p className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in text-theme-1 text-xl delay-75 duration-200">
            How can I help you today?
          </p>
          <div className="h-6" />
          <ThreadSuggestions welcome />
        </div>
      </div>
    </div>
  );
};

type GuyState =
  | "thinking"
  | "idle"
  | "user-typing"
  | "thought"
  | "creating"
  | "created"
  | "user-creating";

export const ComposerGuy: FC<{ type: GuyState | null; fadeIn?: boolean }> = ({
  type,
  fadeIn = true,
}) => {
  switch (type) {
    case "thinking":
      return (
        <img
          src={thinkingGuyImgSrc}
          style={{
            animation: "floatingGuy 1s ease-in-out infinite",
          }}
          className={cn("absolute w-20 h-20 z-100 -top-15")}
        />
      );
    case "thought":
      return (
        <img
          src={thoughtGuyImgSrc}
          className={cn(
            fadeIn && "fade-in slide-in-from-bottom-2 animate-in duration-200",
            "absolute w-20 h-20 z-100 -top-16"
          )}
        />
      );
    case "creating":
      return (
        <img
          src={creatingGuyImgSrc}
          style={{
            animation: "floatingGuy 1s ease-in-out infinite",
          }}
          className={cn("absolute w-20 h-20 z-100 -top-14")}
        />
      );
    case "created":
      return (
        <img
          src={createdGuyImgSrc}
          className={cn(
            fadeIn && "fade-in slide-in-from-bottom-2 animate-in duration-200",
            "absolute w-20 h-20 z-100 -top-14"
          )}
        />
      );
    case "idle":
      return (
        <img
          src={createdGuyImgSrc}
          className={cn(
            fadeIn && "fade-in slide-in-from-bottom-2 animate-in duration-200",
            "absolute w-20 h-20 z-100 -top-14"
          )}
        />
      );
    case "user-typing":
      return (
        <img
          src={thinkingGuyImgSrc}
          className={cn(
            fadeIn && "fade-in slide-in-from-bottom-2 animate-in duration-200",
            "absolute w-20 h-20 z-100 -top-15"
          )}
        />
      );
    case "user-creating":
      return (
        <img
          src={creatingGuyImgSrc}
          className={cn(
            fadeIn && "fade-in slide-in-from-bottom-2 animate-in duration-200",
            "absolute w-20 h-20 z-100 -top-14"
          )}
        />
      );
    default:
      return null;
  }
};

// Context for managing active suggestion
const SuggestionContext = {
  activeSuggestion: null as (typeof HIGHLIGHT_CONFIG)[number] | null,
  setActiveSuggestion: (
    suggestion: (typeof HIGHLIGHT_CONFIG)[number] | null
  ) => {},
};

const ThreadSuggestions: FC<{ welcome?: boolean }> = ({ welcome }) => {
  const [activeSuggestion, setActiveSuggestion] = useState<
    (typeof HIGHLIGHT_CONFIG)[number] | null
  >(null);

  // Update context
  SuggestionContext.activeSuggestion = activeSuggestion;
  SuggestionContext.setActiveSuggestion = setActiveSuggestion;

  const handleSelect = (suggestion: (typeof HIGHLIGHT_CONFIG)[number]) => {
    setActiveSuggestion(suggestion);
    // Focus will be handled by Composer component
  };

  return (
    <div className="flex justify-between w-full relative">
      {!welcome && <div />}
      <div className="flex flex-wrap gap-2 pb-4">
        {HIGHLIGHT_CONFIG.map((s, index) => {
          const isActive = activeSuggestion?.id === s.id;

          return (
            <div
              key={s.id}
              className="fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
              style={{ animationDelay: `${100 + index * 50}ms` }}
            >
              <HighlightButton
                highlight={s}
                isActive={isActive}
                onSelect={handleSelect}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Composer: FC = () => {
  const [activeSuggestion, setActiveSuggestion] = useState<
    (typeof HIGHLIGHT_CONFIG)[number] | null
  >(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const submitToolCallRef = useRef<
    ((config: Record<string, string>) => void) | null
  >(null);

  const [guy, setGuy] = useState<GuyState | null>(null);
  const { selectedSources } = useNotebookContext();
  const [charCount, setCharCount] = useState(0);
  const HIGHLIGHT_CHAR_LIMIT = 2000;
  const CHAT_CHAR_LIMIT = 10000;
  const currentCharLimit = activeSuggestion
    ? HIGHLIGHT_CHAR_LIMIT
    : CHAT_CHAR_LIMIT;

  const isToolCall = (c: MessagePartState) =>
    c.type === "text" &&
    typeof c.text === "string" &&
    c.text.startsWith(`${TOOL_PREFIX}:`);
  const api = useAssistantApi();
  useEffect(() => {
    api.on("thread.run-end", () => {
      const state = api.thread().getState();
      if (state.messages.length < 2) return;
      const lastUserMessage = state.messages[state.messages.length - 2];
      if (
        !lastUserMessage.parts.length ||
        !isToolCall(lastUserMessage.parts[0])
      )
        setGuy("thought");
      else setGuy("created");
    });
  });

  // Track character count
  useEffect(() => {
    const interval = setInterval(() => {
      const currentText = api.composer().getState().text;
      setCharCount((prev) =>
        prev === currentText.length ? prev : currentText.length
      );
    }, 250);
    return () => clearInterval(interval);
  }, [api]);

  const [generationConfig, setGenerationConfig] = useState<
    Record<string, string>
  >({});

  // Subscribe to suggestion changes
  useEffect(() => {
    if (activeSuggestion)
      setGenerationConfig(
        Object.fromEntries(
          Object.entries(activeSuggestion.configPrompt.options).map(
            ([k, v]) => [k, v[0]]
          )
        )
      );
    const interval = setInterval(() => {
      if (SuggestionContext.activeSuggestion !== activeSuggestion) {
        setActiveSuggestion(SuggestionContext.activeSuggestion);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [activeSuggestion]);

  // Focus input when suggestion becomes active
  useEffect(() => {
    if (activeSuggestion && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeSuggestion]);

  const handleCancel = () => {
    SuggestionContext.setActiveSuggestion(null);
    setActiveSuggestion(null);
    setGuy("idle");
  };

  const Icon = activeSuggestion?.icon;
  return (
    <ComposerPrimitive.Root
      className={cn(
        "aui-composer-root relative flex w-full flex-col bg-theme-0"
      )}
    >
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-200">
        <style>{`
      @keyframes floatingGuy {
        0%, 100% { transform: translateY(0); opacity: 1; }
        50% { transform: translateY(-6px); opacity: 0.5; }
      }
      `}</style>
        <AssistantIf
          condition={({ thread }) =>
            thread.isRunning &&
            thread.messages.length > 1 &&
            thread.messages[thread.messages.length - 2].parts.length &&
            !isToolCall(thread.messages[thread.messages.length - 2].parts[0]) &&
            !thread.messages[thread.messages.length - 1].parts.length
          }
        >
          {/* api.on('thread.run-start') doesn't always include the tool call message, so this is the workaround */}
          <ComposerGuy type="thinking" />
        </AssistantIf>
        <AssistantIf
          condition={({ thread }) =>
            thread.isRunning &&
            thread.messages.length > 1 &&
            thread.messages[thread.messages.length - 2].parts.length &&
            isToolCall(thread.messages[thread.messages.length - 2].parts[0])
          }
        >
          {/* api.on('thread.run-start') doesn't always include the tool call message, so this is the workaround */}
          <ComposerGuy type="creating" />
        </AssistantIf>
        <AssistantIf
          condition={({ thread }) =>
            thread.isRunning &&
            thread.messages.length > 1 &&
            thread.messages[thread.messages.length - 2].parts.length &&
            !isToolCall(thread.messages[thread.messages.length - 2].parts[0]) &&
            !!thread.messages[thread.messages.length - 1].parts.length
          }
        >
          {/* api.on('thread.run-start') doesn't always include the tool call message, so this is the workaround */}
          <ComposerGuy type="thought" />
        </AssistantIf>
        <AssistantIf
          condition={({ thread }) =>
            !(
              thread.isRunning &&
              thread.messages.length > 1 &&
              thread.messages[thread.messages.length - 2].parts.length
            )
          }
        >
          <ComposerGuy
            type={guy}
            fadeIn={
              guy === "user-typing" || guy === "user-creating" || guy === "idle"
            }
          />
        </AssistantIf>
      </div>
      {activeSuggestion && (
        <div
          className={cn(
            "mb-2 flex items-center justify-between rounded-lg px-3 py-3 text-sm sm:text-base",
            "transition-all animate-in fade-in slide-in-from-bottom-2",
            activeSuggestion.color.bg,
            activeSuggestion.color.text
          )}
        >
          {/* Left: prompt */}
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-5 w-5" />}
            <span className="font-medium">
              What topics should your <strong>{activeSuggestion.label}</strong>{" "}
              cover?
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Right: inline generation controls */}
            {/* <div className="hidden md:inline-block"> */}
            {activeSuggestion ? (
              <GenerationConfigControls
                options={activeSuggestion.configPrompt.options}
                value={generationConfig}
                onChange={setGenerationConfig}
              />
            ) : null}
            {/* </div> */}

            {/* Cancel */}
            <TooltipIconButton
              tooltip="Cancel"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleCancel}
            >
              <XIcon className="h-3.5 w-3.5" />
            </TooltipIconButton>
          </div>
        </div>
      )}

      <div
        className={cn(
          // include all colors so tailwind doesn't prune them
          "aui-composer-attachment-dropzone flex w-full flex-col rounded-2xl border border-theme bg-theme-0 px-1 pt-2 outline-none transition-all data-[dragging=true]:border-dashed data-[dragging=true]:bg-accent/50",
          "ring-emerald-500 ring-orange-500 ring-blue-500 ring-none",
          activeSuggestion
            ? cn(
                "ring-2 ring-offset-2",
                activeSuggestion.color.border.replace("border-", "ring-")
              )
            : "border-theme has-[textarea:focus-visible]:border-theme has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-theme"
        )}
      >
        <ComposerPrimitive.Input
          ref={inputRef}
          placeholder={
            selectedSources.length === 0
              ? "Select sources in the left panel to get started!"
              : activeSuggestion
              ? `What topics from your sources should this focus on? Leave blank to use all topics.\nThings to try:\n   - The ${activeSuggestion.id} must focus only on key physics topics\n   - Help me prepare for my history exam`
              : "Ask a question about your course materials..." +
                (guy === "user-typing"
                  ? "\nThings to try:\n  - What topics are likely to be on the exam?\n  - Which formulas should I memorize?"
                  : "")
          }
          disabled={selectedSources.length === 0}
          className={cn(
            "aui-composer-input mb-1 max-h-32 min-h-14 w-full resize-none bg-theme-0 px-4 pt-2 pb-3 text-sm outline-none text-theme-0 focus-visible:ring-0 transition",
            selectedSources.length === 0
              ? "select-none pointer-events-none opacity-70" // disable interaction and selection
              : ""
          )}
          rows={1}
          autoFocus
          aria-label="Message input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              // Get current text length directly to avoid stale state
              const currentTextLength = api.composer().getState().text.length;

              // Prevent submission if character limit is exceeded
              if (currentTextLength > currentCharLimit) {
                e.preventDefault();
                return;
              }

              // Handle highlight generation submission
              if (
                activeSuggestion &&
                submitToolCallRef.current &&
                !api.thread().getState().isRunning // don't allow multiple highlight generations at once
              ) {
                e.preventDefault();
                submitToolCallRef.current(generationConfig);
              }
            }
          }}
          onFocus={() => {
            if (activeSuggestion) setGuy("user-creating");
            else if (
              guy === "created" ||
              guy === "thought" ||
              !guy ||
              guy === "idle"
            )
              setGuy("user-typing");
          }}
          onBlur={() => {
            if (guy === "user-typing") setGuy("idle");
          }}
        />
        {/* Character counter */}
        {charCount >= currentCharLimit * 0.75 && (
          <div className="px-4 pb-2">
            <p
              className={cn(
                "text-xs text-right transition-colors",
                charCount > currentCharLimit
                  ? "text-red-500 font-medium"
                  : charCount > currentCharLimit * 0.9
                  ? "text-orange-500"
                  : "text-theme-1"
              )}
            >
              {charCount} / {currentCharLimit} characters
              {charCount > currentCharLimit && (
                <span className="ml-1">(limit exceeded)</span>
              )}
            </p>
          </div>
        )}
        <ComposerAction
          config={generationConfig}
          submitToolCallRef={submitToolCallRef}
          charCount={charCount}
          charLimit={currentCharLimit}
        />
      </div>
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC<{
  submitToolCallRef: React.RefObject<(config: any) => void>;
  config: any;
  charCount: number;
  charLimit: number;
}> = ({ submitToolCallRef, config, charCount, charLimit }) => {
  const api = useAssistantApi();
  const [activeSuggestion, setActiveSuggestion] = useState<
    (typeof HIGHLIGHT_CONFIG)[number] | null
  >(null);

  // Subscribe to suggestion changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (SuggestionContext.activeSuggestion !== activeSuggestion) {
        setActiveSuggestion(SuggestionContext.activeSuggestion);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [activeSuggestion]);

  // Get button background color from suggestion
  const getButtonColor = () => {
    if (!activeSuggestion) return "";

    // Convert border color to background color
    // e.g., "border-emerald-500" -> "bg-emerald-500"
    return activeSuggestion.color.border.replace("border-", "bg-");
  };

  const { selectedSources } = useNotebookContext();

  useEffect(() => {
    submitToolCallRef.current = (config: any) => {
      if (!activeSuggestion) return;
      if (selectedSources.length === 0) return;

      const input = api.composer().getState().text.trim();

      api.thread().append({
        role: "user",
        content: [
          {
            type: "text",
            text: `${TOOL_PREFIX}:${activeSuggestion.label} ${JSON.stringify(
              config || {}
            )} ${input}`,
          },
        ],
      });

      // 🧹 clear input
      api.composer().setText("");

      // 🧹 clear suggestion
      SuggestionContext.setActiveSuggestion(null);
    };

    return () => {
      submitToolCallRef.current = null;
    };
  }, [activeSuggestion, api, selectedSources]);

  return (
    <div className="aui-composer-action-wrapper relative mx-2 mb-2 flex items-center justify-between">
      <div />
      <AssistantIf condition={({ thread }) => !thread.isRunning}>
        {activeSuggestion ? (
          <div className="flex gap-2">
            <SelectedSourcesPill selectedSources={selectedSources} />
            <TooltipProvider>
              <Tooltip open={charCount > charLimit ? undefined : false}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      selectedSources.length === 0 || charCount > charLimit
                    }
                    className={cn(
                      "gap-1.5 rounded-full text-white transition-opacity",
                      getButtonColor(),
                      "hover:opacity-90",
                      charCount > charLimit && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => submitToolCallRef.current?.(config)}
                  >
                    <span className="text-xs font-medium">
                      Create {activeSuggestion.label}
                    </span>
                    <ArrowUpIcon className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                {charCount > charLimit && (
                  <TooltipContent>
                    <p>Message exceeds {charLimit} character limit</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <ComposerPrimitive.Send
            className={cn(
              "flex gap-2",
              selectedSources.length === 0 || charCount > charLimit
                ? "select-none pointer-events-none opacity-70" // disable interaction and selection
                : ""
            )}
          >
            <SelectedSourcesPill selectedSources={selectedSources} />
            <TooltipProvider>
              <Tooltip open={charCount > charLimit ? undefined : false}>
                <TooltipTrigger asChild>
                  <TooltipIconButton
                    tooltip={charCount > charLimit ? "" : "Send message"}
                    side="bottom"
                    variant="default"
                    size="icon"
                    className="size-8 rounded-full shadow-none text-theme-0"
                  >
                    <ArrowUpIcon className="size-4" />
                  </TooltipIconButton>
                </TooltipTrigger>
                {charCount > charLimit && (
                  <TooltipContent>
                    <p>Message exceeds {charLimit} character limit</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </ComposerPrimitive.Send>
        )}
      </AssistantIf>

      <AssistantIf condition={({ thread }) => thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-8 rounded-full shadow-none text-theme-0"
            aria-label="Stop generating"
          >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AssistantIf>
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
      style={{ minHeight: "auto" }}
    >
      <div className="aui-assistant-message-content wrap-break-word px-2 text-theme-0 leading-relaxed">
        <MessagePrimitive.Parts
          components={{
            Text: MarkdownText,
            tools: {
              by_name: {
                ...Object.fromEntries(
                  HIGHLIGHT_CONFIG.map((v) => [v.label, HighlightToolResult])
                ),
                [TRIAL_GATE_TOOL]: TrialGateToolResult,
              },
              Fallback: ToolFallback,
            },
            Empty: AIThinkingAnimation,
          }}
        />
        <MessageError />
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-2 flex">
        <BranchPicker />
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      autohideFloat="single-branch"
      className="aui-assistant-action-bar-root -ml-1 col-start-3 row-start-2 flex gap-1 text-theme-1 data-floating:absolute data-floating:rounded-md data-floating:border data-floating:bg-theme-0 data-floating:p-1 data-floating:shadow-sm"
    >
      <AssistantIf
        condition={({ message }) =>
          message.parts.some((x) => x.type === "text")
        }
      >
        <ActionBarPrimitive.Copy asChild>
          <TooltipIconButton tooltip="Copy">
            <AssistantIf condition={({ message }) => message.isCopied}>
              <CheckIcon />
            </AssistantIf>
            <AssistantIf condition={({ message }) => !message.isCopied}>
              <CopyIcon />
            </AssistantIf>
          </TooltipIconButton>
        </ActionBarPrimitive.Copy>
      </AssistantIf>
      {/* <ActionBarPrimitive.ExportMarkdown asChild>
        <TooltipIconButton tooltip="Export as Markdown">
          <DownloadIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.ExportMarkdown> */}
      {/* <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Refresh">
          <RefreshCwIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload> */}
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      {/* <UserMessageAttachments /> */}

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div className="aui-user-message-content wrap-break-word rounded-2xl bg-muted px-4 py-2.5 text-theme-0">
          <MessagePrimitive.Parts
            components={{
              Text: IntentAwareText,
            }}
          />
        </div>
        <div className="aui-user-action-bar-wrapper -translate-x-full -translate-y-1/2 absolute top-1/2 left-0 pr-2">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker className="aui-user-branch-picker -mr-1 col-span-full col-start-1 row-start-3 justify-end" />
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      {/* <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip="Edit" className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit> */}
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-theme-2">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-theme-0 text-sm outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">Update</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root -ml-2 mr-2 inline-flex items-center text-theme-1 text-xs",
        className
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
